/**
 * @fileoverview File Upload Progress Hook
 * 
 * Manages upload progress tracking, retry logic, and error handling
 * for file upload operations with real-time updates.
 * 
 * Requirements: 1.4, 1.5
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { generateUploadId } from '../types.js';

/**
 * File upload progress hook
 * 
 * @returns {Object} Progress tracking utilities
 */
export function useFileUploadProgress() {
  const [uploads, setUploads] = useState([]);
  const [errors, setErrors] = useState([]);
  const progressIntervals = useRef(new Map());
  const retryTimeouts = useRef(new Map());

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      progressIntervals.current.forEach(interval => clearInterval(interval));
      retryTimeouts.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  /**
   * Initialize uploads for tracking
   * 
   * @param {File[]} files - Files to track
   */
  const initializeUploads = useCallback((files) => {
    const newUploads = files.map((file, index) => ({
      id: generateUploadId(),
      file,
      status: 'pending',
      progress: 0,
      loaded: 0,
      total: file.size,
      startTime: null,
      endTime: null,
      error: null,
      result: null,
      estimatedTimeRemaining: null,
      retryCount: 0,
      index
    }));

    setUploads(newUploads);
    setErrors([]); // Clear previous errors
  }, []);

  /**
   * Update progress for a specific file with enhanced time estimation
   * 
   * @param {number} index - File index
   * @param {Object} updates - Progress updates
   */
  const updateFileProgress = useCallback((index, updates) => {
    setUploads(prevUploads => {
      return prevUploads.map(upload => {
        if (upload.index === index) {
          const updatedUpload = { ...upload, ...updates };

          // Enhanced time estimation with smoothing
          if (updates.progress && updates.progress > 0 && upload.startTime) {
            const elapsed = Date.now() - upload.startTime;
            const progressRatio = updates.progress / 100;
            
            // Use exponential smoothing for better estimation
            const estimatedTotal = elapsed / progressRatio;
            const estimatedRemaining = Math.max(0, estimatedTotal - elapsed);
            
            // Smooth the estimation to avoid jumpy values
            if (upload.estimatedTimeRemaining) {
              const smoothingFactor = 0.3;
              updatedUpload.estimatedTimeRemaining = 
                (smoothingFactor * estimatedRemaining) + 
                ((1 - smoothingFactor) * upload.estimatedTimeRemaining);
            } else {
              updatedUpload.estimatedTimeRemaining = estimatedRemaining;
            }

            // Calculate upload speed
            if (updates.loaded && elapsed > 0) {
              updatedUpload.uploadSpeed = updates.loaded / (elapsed / 1000); // bytes per second
            }
          }

          // Set end time when completed or failed
          if (updates.status === 'completed' || updates.status === 'failed') {
            updatedUpload.endTime = Date.now();
            updatedUpload.estimatedTimeRemaining = 0;

            // Clear any progress interval for this upload
            const intervalId = progressIntervals.current.get(upload.id);
            if (intervalId) {
              clearInterval(intervalId);
              progressIntervals.current.delete(upload.id);
            }

            // Calculate final upload speed
            if (upload.startTime && updatedUpload.endTime) {
              const totalTime = (updatedUpload.endTime - upload.startTime) / 1000;
              updatedUpload.finalUploadSpeed = upload.file.size / totalTime;
            }
          }

          // Set start time when uploading begins
          if (updates.status === 'uploading' && !upload.startTime) {
            updatedUpload.startTime = Date.now();
          }

          return updatedUpload;
        }
        return upload;
      });
    });

    // Handle errors
    if (updates.status === 'failed' && updates.error) {
      const upload = uploads.find(u => u.index === index);
      if (upload) {
        addError({
          id: `error-${upload.id}`,
          uploadId: upload.id,
          fileName: upload.file.name,
          error: updates.error,
          timestamp: Date.now(),
          retryable: true
        });
      }
    }
  }, [uploads]);

  /**
   * Start simulated progress for an upload
   * 
   * @param {string} uploadId - Upload ID
   * @param {number} duration - Expected duration in ms
   */
  const startProgressSimulation = useCallback((uploadId, duration = 30000) => {
    const upload = uploads.find(u => u.id === uploadId);
    if (!upload || upload.status !== 'uploading') return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(90, (elapsed / duration) * 100); // Cap at 90% for simulation

      setUploads(prevUploads => 
        prevUploads.map(u => 
          u.id === uploadId 
            ? { 
                ...u, 
                progress,
                estimatedTimeRemaining: Math.max(0, duration - elapsed)
              }
            : u
        )
      );

      if (progress >= 90) {
        clearInterval(interval);
        progressIntervals.current.delete(uploadId);
      }
    }, 200);

    progressIntervals.current.set(uploadId, interval);
  }, [uploads]);

  /**
   * Add an error to the error list
   * 
   * @param {Object} error - Error object
   */
  const addError = useCallback((error) => {
    setErrors(prevErrors => {
      // Avoid duplicate errors
      const exists = prevErrors.some(e => 
        e.uploadId === error.uploadId && e.error === error.error
      );
      
      if (exists) return prevErrors;
      
      return [...prevErrors, error];
    });
  }, []);

  /**
   * Retry failed uploads
   * 
   * @param {string[]} [uploadIds] - Specific upload IDs to retry, or all failed uploads
   * @returns {Promise<void>}
   */
  const retryFailedUploads = useCallback(async (uploadIds = null) => {
    const uploadsToRetry = uploads.filter(upload => {
      if (upload.status !== 'failed') return false;
      if (uploadIds && !uploadIds.includes(upload.id)) return false;
      return upload.retryCount < 3; // Max 3 retries
    });

    if (uploadsToRetry.length === 0) {
      return;
    }

    // Reset uploads to pending status
    setUploads(prevUploads => 
      prevUploads.map(upload => {
        if (uploadsToRetry.some(retry => retry.id === upload.id)) {
          return {
            ...upload,
            status: 'pending',
            progress: 0,
            error: null,
            retryCount: upload.retryCount + 1,
            startTime: null,
            endTime: null,
            estimatedTimeRemaining: null
          };
        }
        return upload;
      })
    );

    // Remove related errors
    setErrors(prevErrors => 
      prevErrors.filter(error => 
        !uploadsToRetry.some(upload => upload.id === error.uploadId)
      )
    );

    // Emit retry event (would be handled by parent component)
    return { retriedUploads: uploadsToRetry };
  }, [uploads]);

  /**
   * Dismiss an error
   * 
   * @param {string} errorId - Error ID to dismiss
   */
  const dismissError = useCallback((errorId) => {
    if (errorId === 'all') {
      setErrors([]);
    } else {
      setErrors(prevErrors => prevErrors.filter(error => error.id !== errorId));
    }
  }, []);

  /**
   * Clear all uploads and reset state
   */
  const clearUploads = useCallback(() => {
    // Clear all intervals and timeouts
    progressIntervals.current.forEach(interval => clearInterval(interval));
    retryTimeouts.current.forEach(timeout => clearTimeout(timeout));
    progressIntervals.current.clear();
    retryTimeouts.current.clear();

    setUploads([]);
    setErrors([]);
  }, []);

  /**
   * Get upload statistics
   * 
   * @returns {Object} Upload statistics
   */
  const getUploadStats = useCallback(() => {
    const stats = uploads.reduce((acc, upload) => {
      acc.total++;
      acc[upload.status] = (acc[upload.status] || 0) + 1;
      
      if (upload.status === 'completed') {
        acc.totalSize += upload.file.size;
        acc.totalTime += upload.endTime - upload.startTime;
      }
      
      return acc;
    }, {
      total: 0,
      pending: 0,
      uploading: 0,
      completed: 0,
      failed: 0,
      totalSize: 0,
      totalTime: 0
    });

    // Calculate average upload speed
    if (stats.completed > 0 && stats.totalTime > 0) {
      stats.averageSpeed = stats.totalSize / (stats.totalTime / 1000); // bytes per second
    }

    return stats;
  }, [uploads]);

  /**
   * Get progress summary
   * 
   * @returns {Object} Progress summary
   */
  const getProgressSummary = useCallback(() => {
    const stats = getUploadStats();
    const overallProgress = uploads.length > 0 
      ? uploads.reduce((sum, upload) => sum + upload.progress, 0) / uploads.length
      : 0;

    const activeUploads = uploads.filter(u => u.status === 'uploading');
    const estimatedTimeRemaining = activeUploads.length > 0
      ? Math.max(...activeUploads.map(u => u.estimatedTimeRemaining || 0))
      : 0;

    return {
      overallProgress: Math.round(overallProgress),
      completedCount: stats.completed,
      totalCount: stats.total,
      failedCount: stats.failed,
      isComplete: stats.completed + stats.failed === stats.total && stats.total > 0,
      isUploading: stats.uploading > 0,
      estimatedTimeRemaining,
      hasErrors: errors.length > 0
    };
  }, [uploads, errors, getUploadStats]);

  /**
   * Format time remaining for display
   * 
   * @param {number} ms - Time in milliseconds
   * @returns {string} Formatted time string
   */
  const formatTimeRemaining = useCallback((ms) => {
    if (!ms || ms <= 0) return '';
    
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `${seconds}s remaining`;
    
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 60) return `${minutes}m remaining`;
    
    const hours = Math.ceil(minutes / 60);
    return `${hours}h remaining`;
  }, []);

  /**
   * Format upload speed for display
   * 
   * @param {number} bytesPerSecond - Speed in bytes per second
   * @returns {string} Formatted speed string
   */
  const formatUploadSpeed = useCallback((bytesPerSecond) => {
    if (!bytesPerSecond || bytesPerSecond <= 0) return '';
    
    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    let speed = bytesPerSecond;
    let unitIndex = 0;
    
    while (speed >= 1024 && unitIndex < units.length - 1) {
      speed /= 1024;
      unitIndex++;
    }
    
    return `${speed.toFixed(1)} ${units[unitIndex]}`;
  }, []);

  return {
    uploads,
    errors,
    initializeUploads,
    updateFileProgress,
    startProgressSimulation,
    retryFailedUploads,
    dismissError,
    clearUploads,
    getUploadStats,
    getProgressSummary,
    formatTimeRemaining,
    formatUploadSpeed
  };
}
